/**
 * Modo mock das telas de Pipelines (só ativa com REACT_APP_MOCK=true).
 *
 * - seedMockSession(): grava no localStorage o token/usuário fake ANTES do React
 *   montar, para o RequireAuth liberar as rotas privadas.
 * - installMock(api): troca o adapter do axios para responder com as fixtures
 *   locais, sem tocar em nenhum backend.
 *
 * Ambas são no-op quando REACT_APP_MOCK !== 'true'.
 */

import {
    MOCK_USER,
    ADMIN_LIST_RESPONSE,
    BUDGET_COUNT_RESPONSE,
    MENTION_NOTIFICATIONS_RESPONSE,
} from "./sessionFixtures";
import {
    PIPELINE_RESPONSE,
    CRM_TAG_RESPONSE,
    boardForPipeline,
    cardsListForPipeline,
} from "./crmFixtures";
import {
    CARD_RESPONSE,
    COMPANY_RESPONSE,
    CUSTOMER_RESPONSE,
    CARD_TASKS_RESPONSE,
    CARD_ACTIVITIES_RESPONSE,
    AUTOMATION_RESPONSE,
    PIPELINE_TEMPLATES_RESPONSE,
    pipelineDetailFor,
    PIPELINE_ADMINS_RESPONSE,
} from "./crmDetailFixtures";

export const isMockEnabled = () => process.env.REACT_APP_MOCK === "true";

export const seedMockSession = () => {
    if (!isMockEnabled()) return;

    localStorage.setItem("token", "mock-token");
    localStorage.setItem("user", JSON.stringify(MOCK_USER));
    localStorage.setItem("lastPath", "/crm");
};

// Resolve o corpo de resposta a partir da URL da requisição.
// `params` vem do config do axios: como o adapter roda antes da serialização,
// a query nem sempre está na URL (algumas chamadas passam tudo por params).
const resolveMock = (url, params = {}) => {
    const [path, query] = (url || "").split("?");
    // O board é servido por pipeline: /pipeline-stage?pipeline_id=1 devolve o
    // board Comercial, 2 o de Produção.
    const pipelineId = new URLSearchParams(query ?? "").get("pipeline_id")
        ?? params?.pipeline_id
        ?? null;

    if (path.endsWith("/budget/count")) return BUDGET_COUNT_RESPONSE;
    if (path.includes("/mention-notifications")) return MENTION_NOTIFICATIONS_RESPONSE;
    if (path.endsWith("/list")) return ADMIN_LIST_RESPONSE;

    // Pipelines — a ordem importa: checar as rotas mais específicas antes de
    // /pipeline, e as com :id antes das de coleção.
    if (path.endsWith("/pipeline-stage/cards-list")) return cardsListForPipeline(pipelineId);
    if (path.endsWith("/pipeline-stage")) return boardForPipeline(pipelineId);
    if (path.includes("/pipeline-stage-card-task")) return CARD_TASKS_RESPONSE;
    if (path.includes("/pipeline-stage-card-activity")) return CARD_ACTIVITIES_RESPONSE;
    if (path.includes("/pipeline-stage-card/")) return CARD_RESPONSE;
    if (path.endsWith("/pipeline-templates")) return PIPELINE_TEMPLATES_RESPONSE;
    if (path.endsWith("/admins")) return PIPELINE_ADMINS_RESPONSE;
    if (path.includes("/crm-automation")) return AUTOMATION_RESPONSE;
    if (path.endsWith("/crm-tag")) return CRM_TAG_RESPONSE;
    const detailMatch = path.match(/\/pipeline\/(\d+)$/);
    if (detailMatch) return pipelineDetailFor(detailMatch[1]);
    if (path.endsWith("/pipeline")) return PIPELINE_RESPONSE;
    if (/\/company\/\d+$/.test(path)) return COMPANY_RESPONSE;
    if (/\/customer\/\d+$/.test(path)) return CUSTOMER_RESPONSE;
    // Selects de empresa/pessoa do card: uma opção basta para o campo aparecer
    // preenchido em vez de "Selecione...".
    if (path.endsWith("/company")) return { resources: [COMPANY_RESPONSE.resource] };
    if (path.endsWith("/customer")) return { resources: [CUSTOMER_RESPONSE.resource] };

    // Catch-all benigno: evita que qualquer outra chamada quebre a tela.
    // Rotas paginadas esperam `resources` como objeto {data, pagination}.
    if (path.endsWith("/paginate") || path.endsWith("/search")) {
        return {
            resources: {
                data: [],
                pagination: {
                    total: 0,
                    per_page: 15,
                    current_page: 1,
                    last_page: 1,
                    next_page_url: null,
                    prev_page_url: null,
                },
            },
        };
    }

    return { resources: [], data: [], count: 0, total: 0 };
};

export const installMock = api => {
    if (!isMockEnabled()) return;

    api.defaults.adapter = config =>
        Promise.resolve({
            data: resolveMock(config.url, config.params),
            status: 200,
            statusText: "OK (mock)",
            headers: {},
            config,
            request: {},
        });

    // eslint-disable-next-line no-console
    console.info("[mock] Pipelines em modo mock — respostas servidas localmente.");
};
